import React from 'react';
import PropTypes from 'prop-types';
import { Outlet, Navigate, useLocation } from 'react-router-dom';

import { DefaultLayout, GenericError } from '../../components';

import { withAuthentication } from '../authentication/HOCs';
import { Space, EditChat, NewChat, NewSpace, AgenticEditChat, AgenticNewChat } from './pages';
import { SourcesProvider, UsersContext } from '../organization';
import {
  SpacesProvider,
  AssetBuilderProvider,
  AgenticAssetBuilderProvider,
  AgenticConversationsProvider,
  AnswersProvider,
  ConversationsProvider,
  ConversationsContext,
  AgenticConversationsContext,
  QuestionsProvider,
  DatabaseDescriptionProvider,
  SpacesContext,
  TermTranslationsProvider,
  SchedulesProvider
} from './providers';

// Component to conditionally render appropriate conversations provider and context
const ConversationsProviderWrapper = ({ children }) => {
  const location = useLocation();
  const isAgentic = location.pathname.startsWith('/agentic-chats');

  if (isAgentic) {
    return (
      <AgenticConversationsProvider>
        <AgenticConversationsContext.Consumer>
          {(contextProps) => children({ ...contextProps, isAgentic })}
        </AgenticConversationsContext.Consumer>
      </AgenticConversationsProvider>
    );
  }

  return (
    <ConversationsProvider type='asset'>
      <ConversationsContext.Consumer>
        {(contextProps) => children({ ...contextProps, isAgentic })}
      </ConversationsContext.Consumer>
    </ConversationsProvider>
  );
};

ConversationsProviderWrapper.propTypes = {
  children: PropTypes.func.isRequired
};

const chatsRouter = ({ logout, selectOrganization, currentOrganization, organizations }) => ({
  path: '/',
  element: withAuthentication(() => (
    <SpacesProvider>
      <ConversationsProviderWrapper>
        {({
          groupedConversations,
          fetchNextConversationsPage,
          deleteConversation,
          updateConversation,
          openNextConversation,
          isAgentic
        }) => (
          <TermTranslationsProvider>
            <UsersContext.Consumer>
              {({ currentUser, users }) => (
                <SpacesContext.Consumer>
                  {({ spaces }) => (
                    <DefaultLayout
                      currentOrganization={currentOrganization}
                      organizations={organizations}
                      selectOrganization={selectOrganization}
                      groupedConversations={groupedConversations}
                      deleteConversation={deleteConversation}
                      updateConversation={updateConversation}
                      openNextConversation={openNextConversation}
                      fetchNextConversationsPage={fetchNextConversationsPage}
                      user={currentUser}
                      users={users}
                      logout={logout}
                      spaces={spaces}
                      isAgentic={isAgentic}
                    >
                      <SourcesProvider>
                        <QuestionsProvider>
                          <AnswersProvider>
                            <SchedulesProvider>
                              <Outlet />
                            </SchedulesProvider>
                          </AnswersProvider>
                        </QuestionsProvider>
                      </SourcesProvider>
                    </DefaultLayout>
                  )}
                </SpacesContext.Consumer>
              )}
            </UsersContext.Consumer>
          </TermTranslationsProvider>
        )}
      </ConversationsProviderWrapper>
    </SpacesProvider>
  )),
  children: [
    {
      path: 'chats',
      element: (
        <DatabaseDescriptionProvider>
          <AssetBuilderProvider>
            <Outlet />
          </AssetBuilderProvider>
        </DatabaseDescriptionProvider>
      ),
      errorElement: <GenericError />,
      children: [
        {
          path: '',
          element: (
            <ConversationsContext.Consumer>
              {({ conversations }) => (
                <Navigate to={`/chats/${conversations.length > 0 ? conversations[0].id : 'new'}`} />
              )}
            </ConversationsContext.Consumer>
          ),
          errorElement: <GenericError />
        },
        {
          path: ':conversationId',
          element: <EditChat />,
          errorElement: <GenericError />
        },
        {
          path: 'new',
          element: <NewChat />,
          errorElement: <GenericError />
        }
      ]
    },
    {
      path: 'agentic-chats',
      element: (
        <DatabaseDescriptionProvider>
          <AgenticAssetBuilderProvider>
            <Outlet />
          </AgenticAssetBuilderProvider>
        </DatabaseDescriptionProvider>
      ),
      errorElement: <GenericError />,
      children: [
        {
          path: '',
          element: (
            <AgenticConversationsContext.Consumer>
              {({ conversations }) => (
                <Navigate
                  to={`/agentic-chats/${conversations.length > 0 ? conversations[0].id : 'new'}`}
                />
              )}
            </AgenticConversationsContext.Consumer>
          ),
          errorElement: <GenericError />
        },
        {
          path: ':conversationId',
          element: <AgenticEditChat />,
          errorElement: <GenericError />
        },
        {
          path: 'new',
          element: <AgenticNewChat />,
          errorElement: <GenericError />
        }
      ]
    },
    {
      path: 'spaces',
      element: <Outlet />,
      errorElement: <GenericError />,
      children: [
        {
          path: '',
          element: (
            <SpacesContext.Consumer>
              {({ spaces }) => <Navigate to={`/spaces/${spaces.length > 0 && spaces[0].id}`} />}
            </SpacesContext.Consumer>
          ),
          errorElement: <GenericError />
        },
        {
          path: ':spaceId',
          element: <Space />,
          errorElement: <GenericError />
        },
        {
          path: 'new',
          element: <NewSpace />
        }
      ]
    }
  ]
});

export { chatsRouter };
